import { create } from 'zustand'
import { api, ApiError } from '../../shared/lib/api'
import {
  WebSocketClient,
  createWebSocketClient,
  destroyWebSocketClient,
  type ConnectionState,
  type WebSocketMessage,
  type DoseReminderPayload,
  type DoseUpdatePayload,
  type NotificationPayload,
} from './websocket-client'
import type { ActiveNotificationChannel } from '@rondela/shared'

interface NotificationSubscription {
  id: string
  userId: string
  endpoint: string
  deviceName: string | null
  createdAt: string
}

interface InAppNotification {
  id: string
  type: 'dose_reminder' | 'dose_update' | 'notification' | 'error'
  title: string
  body: string
  data?: Record<string, unknown>
  timestamp: Date
  read: boolean
}

interface NotificationsState {
  // Push subscription state
  isSubscribed: boolean // Any subscription exists
  isCurrentBrowserSubscribed: boolean // Current browser is subscribed
  currentBrowserEndpoint: string | null // Current browser's push endpoint
  subscriptions: NotificationSubscription[]
  isLoading: boolean
  error: string | null
  permission: NotificationPermission

  // WebSocket / real-time state
  wsClient: WebSocketClient | null
  connectionState: ConnectionState
  activeChannel: ActiveNotificationChannel

  // In-app notifications
  notifications: InAppNotification[]
  unreadCount: number

  // Actions - Push
  checkStatus: () => Promise<void>
  subscribe: () => Promise<void>
  unsubscribe: () => Promise<void>
  unsubscribeDevice: (subscriptionId: string) => Promise<void>
  clearError: () => void

  // Actions - WebSocket
  connectWebSocket: (groupId: string, userId: string) => void
  disconnectWebSocket: () => void

  // Actions - In-app notifications
  addNotification: (notification: Omit<InAppNotification, 'id' | 'timestamp' | 'read'>) => void
  markAsRead: (notificationId: string) => void
  markAllAsRead: () => void
  clearNotifications: () => void
}

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  // Initial state
  isSubscribed: false,
  isCurrentBrowserSubscribed: false,
  currentBrowserEndpoint: null,
  subscriptions: [],
  isLoading: false,
  error: null,
  permission: 'default',

  wsClient: null,
  connectionState: 'disconnected',
  activeChannel: 'disconnected',

  notifications: [],
  unreadCount: 0,

  // Push actions
  checkStatus: async () => {
    try {
      if ('Notification' in window) {
        set({ permission: Notification.permission })
      }

      set({ isLoading: true, error: null })

      // Get current browser's push subscription endpoint
      let currentEndpoint: string | null = null
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        try {
          const registration = await navigator.serviceWorker.ready
          const subscription = await registration.pushManager.getSubscription()
          currentEndpoint = subscription?.endpoint || null
        } catch {
          // Service worker not ready yet
        }
      }

      const response = await api.get<{
        subscribed: boolean
        subscriptionsCount: number
        subscriptions: NotificationSubscription[]
      }>('/api/notifications/status')

      // Check if current browser is in the subscriptions list
      const isCurrentBrowserSubscribed = currentEndpoint
        ? response.subscriptions.some(sub => sub.endpoint === currentEndpoint)
        : false

      set({
        isSubscribed: response.subscribed,
        isCurrentBrowserSubscribed,
        currentBrowserEndpoint: currentEndpoint,
        subscriptions: response.subscriptions,
        isLoading: false,
      })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al verificar estado de notificaciones'
      set({ error: message, isLoading: false })
    }
  },

  subscribe: async () => {
    try {
      set({ isLoading: true, error: null })

      if ('Notification' in window && Notification.permission === 'default') {
        const permission = await Notification.requestPermission()
        set({ permission })
        if (permission !== 'granted') {
          set({ error: 'Permiso de notificaciones denegado', isLoading: false })
          return
        }
      }

      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        set({ error: 'Tu navegador no soporta notificaciones push', isLoading: false })
        return
      }

      const { vapidPublicKey } = await api.get<{ vapidPublicKey: string }>('/api/notifications/vapid-key')

      const registration = await navigator.serviceWorker.ready

      const keyArray = urlBase64ToUint8Array(vapidPublicKey)
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyArray,
      })

      const subscriptionJson = subscription.toJSON()
      await api.post('/api/notifications/subscribe', {
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys,
        deviceName: getBrowserName(),
      })

      set({ isSubscribed: true, isCurrentBrowserSubscribed: true, isLoading: false })
      get().checkStatus()
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al activar notificaciones'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  unsubscribe: async () => {
    try {
      set({ isLoading: true, error: null })

      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready
        const subscription = await registration.pushManager.getSubscription()

        if (subscription) {
          // Unsubscribe from push manager
          await subscription.unsubscribe()
          // Remove from server (only this device)
          await api.delete('/api/notifications/subscribe', {
            endpoint: subscription.endpoint,
          })
        }
      }

      // Refresh status to update subscriptions list
      set({ isCurrentBrowserSubscribed: false, isLoading: false })
      get().checkStatus()
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al desactivar notificaciones'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  unsubscribeDevice: async (subscriptionId: string) => {
    try {
      set({ isLoading: true, error: null })
      await api.delete(`/api/notifications/subscribe/${subscriptionId}`)
      // Refresh status
      set({ isLoading: false })
      get().checkStatus()
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al eliminar dispositivo'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  clearError: () => set({ error: null }),

  // WebSocket actions
  connectWebSocket: (groupId: string, userId: string) => {
    const { wsClient } = get()

    // Already connected or connecting
    if (wsClient) {
      return
    }

    const client = createWebSocketClient({
      groupId,
      userId,
      onMessage: (message: WebSocketMessage) => {
        handleWebSocketMessage(message, get)
      },
      onConnectionChange: (state: ConnectionState) => {
        const activeChannel = mapConnectionStateToChannel(state, get().isSubscribed)
        set({ connectionState: state, activeChannel })
      },
    })

    set({ wsClient: client })
    client.connect()
  },

  disconnectWebSocket: () => {
    destroyWebSocketClient()
    set({
      wsClient: null,
      connectionState: 'disconnected',
      activeChannel: get().isSubscribed ? 'push' : 'disconnected',
    })
  },

  // In-app notification actions
  addNotification: (notification) => {
    const newNotification: InAppNotification = {
      ...notification,
      id: crypto.randomUUID(),
      timestamp: new Date(),
      read: false,
    }

    set((state) => ({
      notifications: [newNotification, ...state.notifications].slice(0, 50), // Keep max 50
      unreadCount: state.unreadCount + 1,
    }))

    // Show browser notification via service worker (required for mobile browsers)
    if ('Notification' in window && Notification.permission === 'granted' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.showNotification(notification.title, {
          body: notification.body,
          icon: '/icons/icon-192x192.png',
          badge: '/icons/icon-72x72.png',
          tag: newNotification.id,
        })
      }).catch(console.error)
    }
  },

  markAsRead: (notificationId: string) => {
    set((state) => {
      const notification = state.notifications.find((n) => n.id === notificationId)
      if (!notification || notification.read) return state

      return {
        notifications: state.notifications.map((n) =>
          n.id === notificationId ? { ...n, read: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }
    })
  },

  markAllAsRead: () => {
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    }))
  },

  clearNotifications: () => {
    set({ notifications: [], unreadCount: 0 })
  },
}))

// Handle incoming WebSocket messages
function handleWebSocketMessage(
  message: WebSocketMessage,
  get: () => NotificationsState
) {
  const { addNotification } = get()

  switch (message.type) {
    case 'connected':
      console.log('[Notifications] WebSocket connected:', message.payload)
      break

    case 'dose_reminder': {
      const payload = message.payload as DoseReminderPayload
      addNotification({
        type: 'dose_reminder',
        title: 'Recordatorio de medicamento',
        body: `${payload.petNombre}: ${payload.medicationNombre}`,
        data: { doseId: payload.doseId, medicationId: payload.medicationId },
      })
      break
    }

    case 'dose_update': {
      const payload = message.payload as DoseUpdatePayload
      const statusText = payload.status === 'taken' ? 'administrada' : payload.status === 'skipped' ? 'omitida' : 'vencida'
      addNotification({
        type: 'dose_update',
        title: `Dosis ${statusText}`,
        body: `${payload.petNombre}: ${payload.medicationNombre}${payload.takenByNombre ? ` por ${payload.takenByNombre}` : ''}`,
        data: { doseId: payload.doseId, status: payload.status },
      })
      break
    }

    case 'notification': {
      const payload = message.payload as NotificationPayload
      addNotification({
        type: 'notification',
        title: payload.title,
        body: payload.body,
        data: payload.data,
      })
      break
    }

    case 'error':
      console.error('[Notifications] WebSocket error:', message.payload)
      break

    default:
      console.log('[Notifications] Unknown message type:', message.type)
  }
}

// Map connection state to active channel
function mapConnectionStateToChannel(
  state: ConnectionState,
  hasPushSubscription: boolean
): ActiveNotificationChannel {
  switch (state) {
    case 'connected':
      return 'websocket'
    case 'connecting':
    case 'reconnecting':
      return hasPushSubscription ? 'push' : 'polling'
    case 'disconnected':
      return hasPushSubscription ? 'push' : 'disconnected'
  }
}

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

// Helper function to get browser name from user agent
function getBrowserName(): string {
  const ua = navigator.userAgent
  let browserName = 'Navegador desconocido'
  let os = ''

  // Detect OS
  if (ua.includes('Android')) os = 'Android'
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS'
  else if (ua.includes('Windows')) os = 'Windows'
  else if (ua.includes('Mac')) os = 'Mac'
  else if (ua.includes('Linux')) os = 'Linux'

  // Detect Browser (order matters - check more specific first)
  if (ua.includes('OPR') || ua.includes('Opera')) browserName = 'Opera'
  else if (ua.includes('Edg')) browserName = 'Edge'
  else if (ua.includes('Chrome')) browserName = 'Chrome'
  else if (ua.includes('Safari')) browserName = 'Safari'
  else if (ua.includes('Firefox')) browserName = 'Firefox'

  return os ? `${browserName} (${os})` : browserName
}
